/**
 * obsNoteMoveTargets — catalogue of Observation note boxes for the cross-page Move picker.
 *
 * Clinicians can relocate a note card from one box to another within the OBS module
 * (e.g. Self-Report → a task's General Observation Notes, or the reverse). Each
 * destination is addressed by a stable string id parsed by parseObsBoxId in
 * useObservationStageData (task:<taskId>:masterNotes or general:<panelId>:notes).
 */

import { GENERAL_PANELS } from '../config/generalObservationsConfig';
import { isObservationTaskAdministered } from '../../../../utils/observationTaskAdministration';

/**
 * Resolve a task config entry from a selectedTaskIds entry. Firestore and local
 * state may store ids as numbers or strings depending on hydration path, so we
 * compare loosely rather than with strict === only.
 *
 * @param {Array<{id: number|string, shortTitle: string}>} tasks
 * @param {number|string} selectedId
 * @returns {object|undefined}
 */
function findTaskBySelectedId(tasks, selectedId) {
  return tasks.find(
    (t) => t.id === selectedId || String(t.id) === String(selectedId),
  );
}

/**
 * Build the list of move destinations for StructuredNotesEditor's Move picker.
 *
 * @param {object} options
 * @param {Array<number|string>} options.selectedTaskIds  Tasks in this assessment
 * @param {Array<{id: number|string, shortTitle: string}>} options.tasks  Full task catalogue
 * @param {object} [options.allTaskData]  Per-task data, used to skip tasks marked
 *   "Not administered" — their notes boxes are disabled, so they must not be
 *   offered as move destinations.
 * @param {boolean} [options.excludeSensoryPanel=false]  When true, omit the Sensory
 *   (B4) general panel — used on Task Administration where that panel is hidden
 *   from the sidebar but Self-Report remains available.
 * @returns {Array<{id: string, label: string, group: string}>}
 */
export function buildObsNoteMoveTargets({
  selectedTaskIds,
  tasks,
  allTaskData = {},
  excludeSensoryPanel = false,
}) {
  const targets = [];

  for (const selectedId of selectedTaskIds || []) {
    const task = findTaskBySelectedId(tasks, selectedId);
    if (!task) continue;
    // A task marked "Not administered" has a disabled notes box, so a note
    // cannot be dropped into it. Omit it from the destination list.
    if (!isObservationTaskAdministered(allTaskData[selectedId] ?? allTaskData[task.id])) continue;
    // Always use the canonical task.id from config so ids match allTaskData keys.
    targets.push({
      id: `task:${task.id}:masterNotes`,
      label: task.shortTitle,
      group: 'Tasks',
    });
  }

  for (const panel of GENERAL_PANELS) {
    if (excludeSensoryPanel && panel.id === 'sensory') continue;
    targets.push({
      id: `general:${panel.id}:notes`,
      label: panel.title,
      group: 'General Observations',
    });
  }

  return targets;
}
